<template>
  <div class="shipping-holder">

    <h1>{{ t.shipping_and_payment }}</h1>

    <form method="post" id="webshop-shipping" v-on:submit.prevent>

      <div class="row">

        <div class="col-md-6">

          <fieldset>

            <legend>{{ t.shipping }}</legend>

            <div v-for="shipping in orderData.shippingArray" :key="shipping.id">
              <label class="radio-container">
                <input type="radio" name="shipping_type"
                       v-on:change="calcShipping"
                       v-model="formData.shippingId"
                       :value="shipping.id"
                       :checked="formData.shippingId">

                <span class="checkmark"></span> {{ shipping.name }}
              </label>
              <div class="shipping-type-description" v-if="formData.shippingId === shipping.id" v-html="shipping.description"></div>
            </div>

            <p class="input-error-hint" v-if="error.shipping_error" v-html="error.shipping_error"></p>

          </fieldset>

          <fieldset>

            <legend>{{ t.payment }}</legend>

            <div v-if="!formData.shippingId">{{ t.please_select_shipping }}</div>

            <div v-for="payment in orderData.paymentArray" :key="payment.id" v-show="payment.shipping_id === parseInt(formData.shippingId)">
              <label class="radio-container">
                <input type="radio" :name="'payment_type_' + payment.shipping_id"
                       v-on:change="calcShipping"
                       v-model="formData.paymentId"
                       :value="payment.id"
                       :checked="formData.paymentId">

                <span class="checkmark"></span> {{ payment.name }}
              </label>
              <div class="payment-type-description" v-if="formData.paymentId === payment.id" v-html="payment.description"></div>
            </div>

            <p class="input-error-hint" v-if="error.payment_error" v-html="error.payment_error"></p>

          </fieldset>

          <fieldset v-if="priceTableArray.length">
            <legend>{{ t.shipping_price }}</legend>

            <table class="table table-striped table-bordered">
              <thead>
                <tr>
                  <th v-if="settingData.isWeightBasedShipping === '1'">{{ t.from_weight }}</th>
                  <th v-if="settingData.isWeightBasedShipping === '1'">{{ t.to_weight }}</th>
                  <th v-if="settingData.isFrontendPriceNet === '1'">{{ t.net_price }}</th>
                  <th v-if="settingData.isFrontendPriceNet === '0'">{{ t.gross_price }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(priceItem, priceIndex) in priceTableArray" :key="priceIndex">
                  <td v-if="settingData.isWeightBasedShipping === '1'">{{ priceItem.from_weight }} kg</td>
                  <td v-if="settingData.isWeightBasedShipping === '1'">{{ priceItem.to_weight }} kg</td>
                  <td>{{ formatNumber(roundNumber(priceItem.price)) }}</td>
                </tr>
              </tbody>
            </table>

          </fieldset>

          <div class="webshop-shipping-price" v-if="formData.shippingId">

            <p v-if="settingData.isWeightBasedShipping" class="products-weight">
              <strong>{{ t.products_weight }}</strong>: {{ productData.products_weight }} kg
            </p>

            <p v-if="shippingName && paymentName && !shippingCustom" class="products-shipping-payment">
              <strong>{{ shippingName }}, {{ paymentName }}</strong>: {{ shippingPrice ? formatNumber(shippingPrice) + ' + ' + t.vat : t.free }}{{ paymentPrice ? ', ' + formatNumber(paymentPrice) + ' + ' + t.vat : '' }}
            </p>

            <p v-if="shippingCustom" class="products-shipping-payment">
              <strong class="text-danger">{{ t.custom_shipping_method }}</strong>
            </p>

            <p v-if="orderPrice" class="products-order-price">
              <strong>{{ t.cart_total }}</strong>: {{ formatNumber(orderPrice) }} + {{ t.vat }}
            </p>

          </div>

        </div>

        <div class="col-md-6">

          <fieldset v-if="showShippingForm">

            <legend>{{ t.shipping_detail }}</legend>

            <div class="form-group" v-if="formData.shippingArray.length">
              <label class="control-label" for="shipping_id"><strong>{{ t.shipping_addresses }}</strong></label>
              <select id="shipping_id" class="form-control" name="shipping_id"
                      v-on:change="shippingChange"
                      v-model="formData.shippingAddress.shipping_id">
                <option value="0">{{ t.create_new_address }}</option>
                <option v-for="address in formData.shippingArray" :key="address.id" :value="address.id">{{ address.name }}</option>
              </select>
            </div>

            <div class="form-group" v-if="formData.countryArray.length">
              <label class="control-label" for="shipping_country">{{ t.shipping_country }}</label>
              <select id="shipping_country" class="form-control" name="shipping_country"
                      v-on:change="calcShipping"
                      v-model="formData.shippingAddress.shipping_country"
                      v-bind:class="{ 'is-invalid' : error.shipping_country }"
                      @focus="error.shipping_country = null">

                <option v-for="country in formData.countryArray" :key="country.id" :value="country.id">{{ country.name }}</option>
              </select>
            </div>

            <div class="form-group">
              <label class="control-label" for="shipping_name">{{ t.shipping_name }}*</label>
              <input type="text" id="shipping_name" class="form-control" name="shipping_name"
                     v-bind:placeholder="t.shipping_name"
                     v-model="formData.shippingAddress.shipping_name"
                     v-bind:class="{ 'is-invalid' : error.shipping_name }"
                     @focus="error.shipping_name = null">

              <p class="input-error-hint" v-if="error.shipping_name" v-html="error.shipping_name"></p>
            </div>

            <div class="form-group">
              <label class="control-label" for="shipping_postal">{{ t.shipping_postal }}*</label>
              <input type="text" id="shipping_postal" class="form-control" name="shipping_postal"
                     v-bind:placeholder="t.shipping_postal"
                     v-model="formData.shippingAddress.shipping_postal"
                     v-bind:class="{ 'is-invalid' : error.shipping_postal }"
                     @focus="error.shipping_postal = null">

              <p class="input-error-hint" v-if="error.shipping_postal" v-html="error.shipping_postal"></p>
            </div>

            <div class="form-group">
              <label class="control-label" for="shipping_city">{{ t.shipping_city }}*</label>
              <input type="text" id="shipping_city" class="form-control" name="shipping_city"
                     v-bind:placeholder="t.shipping_city"
                     v-model="formData.shippingAddress.shipping_city"
                     v-bind:class="{ 'is-invalid' : error.shipping_city }"
                     @focus="error.shipping_city = null">

              <p class="input-error-hint" v-if="error.shipping_city" v-html="error.shipping_city"></p>
            </div>

            <div class="form-group">
              <label class="control-label" for="shipping_address">{{ t.shipping_address }}*</label>
              <input type="text" id="shipping_address" class="form-control" name="shipping_address"
                     v-bind:placeholder="t.shipping_address"
                     v-model="formData.shippingAddress.shipping_address"
                     v-bind:class="{ 'is-invalid' : error.shipping_address }"
                     @focus="error.shipping_address = null">

              <p class="input-error-hint" v-if="error.shipping_address" v-html="error.shipping_address"></p>
            </div>

            <div class="form-group">
              <label class="control-label" for="shipping_door">{{ t.shipping_door }}</label>
              <input type="text" id="shipping_door" class="form-control" name="shipping_door"
                     v-bind:placeholder="t.shipping_door"
                     v-model="formData.shippingAddress.shipping_door"
                     v-bind:class="{ 'is-invalid' : error.shipping_door }"
                     @focus="error.shipping_door = null">
            </div>

          </fieldset>

        </div>

      </div>

    </form>

    <div class="webshop-step-holder">

      <div class="webshop-step-left">
        <div class="step-button">
          <router-link :to="{ name: 'Customer' }" class="btn btn-primary">
            <i class="fa fa-arrow-left" aria-hidden="true"></i> {{ t.back }}
          </router-link>
        </div>
      </div>

      <div class="webshop-step-right">
        <div class="step-button">
          <button class="btn btn-primary" v-on:click="saveShippingForm">{{ t.next }} <i class="fa fa-arrow-right" aria-hidden="true"></i></button>
        </div>
      </div>

    </div>

  </div>
</template>


<script>
import {mapGetters} from "vuex";

export default {
  name: 'Shipping',
  data() {
    return {
      // Szállítás adatlap
      showShippingForm: false,
      // Ártáblázat (Súly, Ország)
      priceTableArray: [],
      // Szállítási és Fizetés
      shippingPrice: 0,
      shippingName: '',
      shippingCustom: '',
      paymentName: '',
      paymentPrice: 0,
      // Kosár értékse
      orderPrice: 0
    }
  },
  computed: {
    ...mapGetters({
      t: "getLanguageData",
      error: "getErrorData",
      userData: "getUserData",
      orderData: "getOrderData",
      productData: "getProductData",
      settingData: "getSettingData",
      formData: "getFormData",
      currentStep: "getCurrentStep",
    }),
  },
  methods: {
    saveShippingForm: function (){
      let $this = this;

      let formData = {
        'page'                : 'Shipping',
        'method'              : 'Save',
        'formData'            : $this.formData
      };

      $this.$store.dispatch('setData', formData);
    },
    calcShipping: function (){
      let _this               = this;
      let $priceTableArray    = this.orderData.shippingCountryWeightArray;
      let $paymentTableArray  = this.orderData.paymentCountryPriceArray;
      let $shippingAddress    = this.formData.shippingAddress;
      let $shippingType       = this.getArrayItem(this.orderData.shippingArray, this.formData.shippingId);
      let $paymentType        = this.getArrayItem(this.orderData.paymentArray, this.formData.paymentId);
      let $isCountryBased     = this.settingData.isCountryBasedShipping;
      let $isWeightBased      = this.settingData.isWeightBasedShipping;
      let $productsWeight     = this.productData.products_weight;
      let $productsPrice      = this.productData.total.net;

      // Reset
      _this.shippingName    = '';
      _this.paymentName     = '';
      _this.shippingCustom  = this.orderData.shippingCustom; // Egyedi szállítás, utólag megadott ár!
      _this.shippingPrice   = 0;
      _this.paymentPrice    = 0;
      _this.priceTableArray = [];
      _this.showShippingForm= false;

      _this.error.shipping_error  = '';
      _this.error.payment_error   = '';

      if(!$shippingType){
        return false;
      }

      // Shipping Name
      _this.shippingName = $shippingType.name;

      // Shipping Form
      if(parseInt($shippingType.is_shipping_required)){
        this.showShippingForm = true;
      }

      // Payment Name
      if($paymentType){
        _this.paymentName = $paymentType.name;
      }

      console.log('1: ' +  _this.shippingPrice);


      if($paymentType && $shippingType){
        _this.shippingPrice = $shippingType.net_price + ($paymentTableArray.length ? 0 : $paymentType.net_price);
        _this.orderPrice = _this.shippingPrice + $productsPrice;
      }

      console.log('2: ' +  _this.shippingPrice, $shippingType);

      // Utánvét ár kalkuláció | Egyedi szállítás árak
      _this.calcPaymentPrice($paymentTableArray, $shippingType, $paymentType, $productsPrice);

      console.log('3: ' +  _this.shippingPrice);

      // Ha egyik sincs, akkor nem jelenítek meg semmit!
      if(!$isWeightBased && !$isCountryBased){
        return false;
      }

      // Ha Country Based és nincs kiválasztva még ország...
      if($isCountryBased && !$shippingAddress.shipping_country){
        return false;
      }

      console.log('DIE2');

      // Nullázom
      _this.shippingPrice = 0;

      // Price Table Show
      if(!$priceTableArray.length){
        alert('Nincs beállítva egyik szállítási módnál sem az ártáblázat!');
      }

      let $shippingCountryId  = $shippingAddress.shipping_country.toString();
      let $shippingTypeId     = $shippingType.id.toString();

      for(let i in $priceTableArray){
        let $priceLine = $priceTableArray[i];

        // Ország szerinti választás
        if($isCountryBased){
          if($shippingCountryId === $priceLine.country &&
              $shippingTypeId === $priceLine.shipping_id){
            _this.priceTableArray.push($priceLine);
          }
        }else{
          if($shippingTypeId === $priceLine.shipping_id){
            _this.priceTableArray.push($priceLine);
          }
        }
      }

      // Ár számolások
      if(_this.priceTableArray.length){
        let $priceLine = null;

        for(let i in _this.priceTableArray){
          $priceLine = _this.priceTableArray[i];

          if($isCountryBased) {
            // Súly figyelés
            if($isWeightBased){
              if(parseFloat($priceLine.from_weight) <= $productsWeight && $productsWeight <= parseFloat($priceLine.to_weight)){
                _this.shippingPrice = $priceLine.price;
              }
            }else{
              // Ha nincs súly, akkor utolsó ár ami meglett adva
              _this.shippingPrice = $priceLine.price;
            }
          }else{
            // Súly figyelés
            if($isWeightBased){
              if(parseFloat($priceLine.from_weight) <= $productsWeight && $productsWeight <= parseFloat($priceLine.to_weight)){
                _this.shippingPrice = $priceLine.price;
              }
            }else{
              _this.shippingPrice = $priceLine.price;
            }
          }
        }

        // Ha nem találtunk árat Súlynál, akkor: utolsó ár * súly
        if($priceLine && _this.shippingPrice === 0 && $isWeightBased){
          _this.shippingPrice = $priceLine.price * $productsWeight;
        }
      }

      // Szállítás ingyenes
      if(parseFloat($shippingType.free_after_total) <= $productsPrice){
        _this.shippingPrice = 0;
      }

      // Kerekítés
      if(_this.shippingPrice > 0){
        _this.shippingPrice = _this.roundNumber(_this.shippingPrice);
      }

      if($paymentType){
        // Fizetési költség ingyenes
        if(parseFloat($paymentType.free_after_total) <= $productsPrice){
          _this.shippingPrice += 0;
        }else{
          // Kerekítés
          _this.shippingPrice += _this.roundNumber(parseFloat($paymentType.net_price));
        }
      }

      // Kosár értéke
      _this.orderPrice = $productsPrice + _this.shippingPrice;

      // Utánvét ár kalkuláció
      _this.calcPaymentPrice($paymentTableArray, $shippingType, $paymentType, $productsPrice);
    },
    calcPaymentPrice: function ($paymentTableArray, $shippingType, $paymentType, $productsPrice){
      let _this = this;
      let $orderGrossPrice = _this.productData.total.gross;
      let $orderNetPrice = _this.productData.total.net;
      let $shippingGrossPrice = $shippingType.gross_price;
      let $totalGrossPrice = $orderGrossPrice + $shippingGrossPrice;
      let $compareGrossPrice = 0;
      let $currentPaymentItem = null;

      // Fizetési módnál, ha van ártáblázat, akkor Kosár értékétől függően lesz plusz költsége!
      if($paymentTableArray.length && $paymentType){
        for (let p in $paymentTableArray){
          let $paymentItem = $paymentTableArray[p];
          if($paymentType.id === $paymentItem.payment_id){
            if(!$paymentItem.is_percent){
              $compareGrossPrice = $totalGrossPrice + $paymentItem.gross_price;
            } else {
              $compareGrossPrice = $totalGrossPrice * (1 + $paymentItem.price / 100);
            }

            if($compareGrossPrice >= $paymentItem.from_price && $compareGrossPrice <= $paymentItem.to_price){
              $currentPaymentItem = $paymentItem;
              break;
            }
          }
        }

        // Ha találtunk fizetési mód árat @see PHP: Payment -> getDetailPrice()
        if($currentPaymentItem){
          // Free
          if(parseFloat($currentPaymentItem.free_after_total) <= $orderNetPrice){
            _this.paymentPrice = 0;
          } else {
            if(!$currentPaymentItem.is_percent){
              _this.orderPrice = _this.orderPrice + $currentPaymentItem.price;
              _this.paymentPrice = $currentPaymentItem.price;
            } else {
              // Ha százalékos az ár! (netto payment/total meghatározás)
              _this.paymentPrice = ($compareGrossPrice - $totalGrossPrice);
              _this.paymentPrice = _this.roundNumber(_this.paymentPrice); //  - (_this.paymentPrice / 100 * $currentPaymentItem.tax)
              _this.orderPrice = _this.orderPrice + _this.paymentPrice;
            }
          }
        }
      }

      // Ha van szállítás korlátozott, akkor nem lesz shipping és payment price és only termék ár a végösszeg.
      if(_this.shippingCustom === 1 && $shippingType.is_custom === 1){
        _this.orderPrice    = $productsPrice;
        _this.shippingPrice = 0;
        _this.paymentPrice  = 0;
      } else {
        _this.shippingCustom = 0;
      }
    },
    shippingChange: function(){
      let $addressId = this.formData.shippingAddress.shipping_id;

      if(!$addressId){
        return true;
      }

      let $selectedAddress = this.getArrayItem(this.formData.shippingArray, $addressId);

      if($selectedAddress !== null){
        if($selectedAddress.shipping_country)
          this.formData.shippingAddress.shipping_country  = $selectedAddress.shipping_country;

        this.formData.shippingAddress.shipping_name     = $selectedAddress.shipping_name;
        this.formData.shippingAddress.shipping_postal   = $selectedAddress.shipping_postal;
        this.formData.shippingAddress.shipping_city     = $selectedAddress.shipping_city;
        this.formData.shippingAddress.shipping_address  = $selectedAddress.shipping_address;
        this.formData.shippingAddress.shipping_door     = $selectedAddress.shipping_door;
      }

      this.$store.commit("setFormData",  this.formData);
    },
    getArrayItem: function ($addressArray, $addressId){
      for(let i in $addressArray){
        let item = $addressArray[i];
        if(parseInt(item.id) === parseInt($addressId)){
          return item;
        }
      }
      return null;
    },
    formatNumber: function (number){
      let formatter = new Intl.NumberFormat('hu-HU', {
        style: 'currency',
        currency: this.orderData.currencyCode,
        minimumFractionDigits: 0
      });

      return formatter.format(number);
    },
    roundNumber: function (number){
      let _this = this;
      let $productPriceNetOrGross = _this.settingData.productPriceNetOrGross;

      let $ten = _this.orderData.currencyTen;
      let $round = _this.orderData.currencyRound;
      let $precision = _this.orderData.currencyNetPrecision;

      if($productPriceNetOrGross === '1'){
        $precision = _this.orderData.currencyGrossPrecision;
      }

      // Round
      if($round === 0){
        return parseFloat((Math.round(number * $ten) / $ten).toFixed($precision));
      }

      // Ceil
      if($round === 1){
        return parseFloat((Math.ceil(number * $ten) / $ten).toFixed($precision));
      }

      // Floor
      if($round === 2){
        return parseFloat((Math.floor(number * $ten) / $ten).toFixed($precision));
      }
    }
  },
  created() {
    /** Betöltés utáni számolás / megjelenítés */
    this.calcShipping();

    window.scrollTo({ top: 0, behavior: 'smooth' });

    this.$store.commit("setCurrentStep", 'Shipping');
  },
  updated() {
    // Ha "saveShipping" lefutott és minden rendben volt, akkor Step is változott, tehát haladunk előre...
    if(this.currentStep === 'Summary'){
      this.$router.push({ name: 'Summary' });
    }
  }
}
</script>